import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

import { globalLogin } from "../auth/authApiLocalClient";
import { pollWebLoginLink, startWebLoginLink } from "../auth/registrationApiClient";

// Matches registration-api's own LINK_TTL (~65s) with headroom, same value
// kwms-web's own WebLoginQr.tsx uses.
const ROTATE_SECONDS = 60;
const POLL_MS = 2 * 1000;

interface WebLoginQrProps {
  /** Called once the link is approved AND this instance's own login gate has passed -- never for a rejected local login, see below. */
  onApproved: (accessToken: string, phone: string) => void;
}

/**
 * Rotating QR, same ceremony as kwms-web's own WebLoginQr.tsx (kwms-android
 * scans it -- there's no separate nodeowner mobile app, owners register
 * and hold identity through the same platform-wide app everyone else
 * does). Trimmed of the countdown-ring visual polish kwms-web has --
 * revisit once this cabinet has a real design pass, not before.
 */
export function WebLoginQr({ onApproved }: WebLoginQrProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const currentLinkId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval> | undefined;
    let rotateInterval: ReturnType<typeof setInterval> | undefined;

    async function startNewLink() {
      clearInterval(pollInterval);
      clearInterval(rotateInterval);
      try {
        const link = await startWebLoginLink();
        if (cancelled) return;
        currentLinkId.current = link.linkId;
        const dataUrl = await QRCode.toDataURL(link.qrContent, { width: 240, margin: 1 });
        if (cancelled) return;
        setQrDataUrl(dataUrl);
        setError(null);

        pollInterval = setInterval(() => poll(link.linkId), POLL_MS);
        rotateInterval = setInterval(startNewLink, ROTATE_SECONDS * 1000);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Не удалось получить QR-код");
      }
    }

    async function poll(linkId: string) {
      if (linkId !== currentLinkId.current) return;
      const result = await pollWebLoginLink(linkId).catch(() => null);
      if (cancelled || linkId !== currentLinkId.current || !result) return;

      if (result.status === "approved") {
        clearInterval(pollInterval);
        clearInterval(rotateInterval);
        const admitted = await globalLogin(result.tokens.accessToken);
        if (cancelled) return;
        if (!admitted) {
          setError("Нет доступа к этому кабинету");
          startNewLink();
          return;
        }
        onApproved(result.tokens.accessToken, result.tokens.phone);
      } else if (result.status === "not_found") {
        startNewLink();
      }
    }

    startNewLink();
    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      clearInterval(rotateInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onApproved is
    // stable enough for this screen's lifetime, same reasoning as kwms-web's own.
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
      {qrDataUrl ? (
        <img src={qrDataUrl} alt="QR для входа" width={200} height={200} style={{ borderRadius: "8px" }} />
      ) : (
        <p className="status-text">Загружаем…</p>
      )}
      <p className="status-text" style={{ marginBottom: 0 }}>
        Отсканируйте камерой приложения kwms
      </p>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
